/**
 * Empirical-formula arithmetic: from amounts of each element to the smallest whole-number
 * ratio, and from an empirical formula to a molecular formula.
 *
 * ## The method
 *
 * 1. Convert each element's mass (or its percentage of a 100 g sample) to moles.
 * 2. Divide every amount by the smallest, giving mole ratios with the smallest at 1.
 * 3. If a ratio is not near a whole number, multiply all of them by the smallest integer
 *    k that makes every one near a whole number (1.5 → ×2, 1.33 → ×3, 1.25 → ×4 …).
 *
 * "Near" is a tolerance on k × ratio, 0.1 by default — the rule of thumb in general
 * chemistry texts (round 2.93 to 3, but do not round 2.5 or 1.33). Experimental
 * percentages are rounded, so exact integers are not expected. Once a multiplier above 1
 * is needed, the fit must be within half that tolerance: otherwise a large enough
 * multiplier would "fit" almost any ratio (√2 × 5 = 7.07).
 */

import { getElement } from "@/lib/tool-kit/chemistry/elements";

export interface RatioInput {
  symbol: string;
  /** Moles of the element (or any quantity proportional to moles). */
  amount: number;
}

export interface RatioRow {
  symbol: string;
  amount: number;
  /** amount ÷ smallest amount. */
  ratio: number;
  /** ratio × multiplier. */
  scaled: number;
  /** The whole-number subscript. */
  subscript: number;
  /** |scaled − subscript|: how far the data sit from a whole number. */
  deviation: number;
}

export interface RatioResult {
  multiplier: number;
  rows: RatioRow[];
  /** Largest deviation among the rows. */
  worstDeviation: number;
}

export const DEFAULT_TOLERANCE = 0.1;
export const MAX_MULTIPLIER = 8;

/**
 * Smallest-multiplier whole-number ratio, or null when no multiplier up to
 * `MAX_MULTIPLIER` brings every element within `tolerance` of a whole number.
 */
export function wholeNumberRatio(
  inputs: readonly RatioInput[],
  tolerance = DEFAULT_TOLERANCE
): RatioResult | null {
  if (inputs.length === 0 || inputs.some((i) => !(i.amount > 0))) {
    return null;
  }
  const smallest = Math.min(...inputs.map((i) => i.amount));
  const ratios = inputs.map((i) => i.amount / smallest);
  for (let k = 1; k <= MAX_MULTIPLIER; k++) {
    const scaled = ratios.map((r) => r * k);
    const subscripts = scaled.map((s) => Math.round(s));
    // A multiplier makes any ratio look closer to a whole number, so the fit is held
    // to half the tolerance once one is needed.
    const allowed = k === 1 ? tolerance : tolerance / 2;
    const fits = scaled.every(
      (s, i) => subscripts[i] >= 1 && Math.abs(s - subscripts[i]) <= allowed
    );
    if (fits) {
      const rows = inputs.map((input, i) => ({
        symbol: input.symbol,
        amount: input.amount,
        ratio: ratios[i],
        scaled: scaled[i],
        subscript: subscripts[i],
        deviation: Math.abs(scaled[i] - subscripts[i]),
      }));
      return {
        multiplier: k,
        rows,
        worstDeviation: Math.max(...rows.map((r) => r.deviation)),
      };
    }
  }
  return null;
}

export interface MolecularResult {
  /** M ÷ empirical-formula mass. */
  multiple: number;
  /** The nearest whole number (at least 1). */
  n: number;
  /** True when `multiple` is within 0.1 of a whole number. */
  consistent: boolean;
}

/** The whole-number multiple that takes the empirical formula to the molecular one. */
export function molecularMultiple(
  molarMass: number,
  empiricalMass: number
): MolecularResult | null {
  if (!(molarMass > 0 && empiricalMass > 0)) {
    return null;
  }
  const multiple = molarMass / empiricalMass;
  const n = Math.max(1, Math.round(multiple));
  return { multiple, n, consistent: Math.abs(multiple - n) <= 0.1 };
}

/** Plain formula text for a list of [symbol, subscript] pairs: C3H4O3. */
export function formulaText(
  pairs: readonly { symbol: string; subscript: number }[]
): string {
  return pairs
    .map(({ symbol, subscript }) =>
      subscript === 1 ? symbol : `${symbol}${subscript}`
    )
    .join("");
}

/* ------------------------------------------------------------ pipelines */

export interface ElementMass {
  symbol: string;
  /** Grams, or percent of a 100 g sample — the arithmetic is identical. */
  mass: number;
}

export interface EmpiricalStep extends RatioRow {
  /** Input mass (or percent). */
  mass: number;
  /** Atomic weight used, g/mol. */
  weight: number;
}

export type EmpiricalError =
  | { code: "tooFew" }
  | { code: "unknownElement"; symbol: string }
  | { code: "duplicateElement"; symbol: string }
  | { code: "badMass"; symbol: string }
  | { code: "noRatio" };

export interface EmpiricalResult {
  steps: EmpiricalStep[];
  multiplier: number;
  worstDeviation: number;
  /** C3H4O3 */
  formula: string;
  /** Sum of subscript × atomic weight, g/mol. */
  formulaMass: number;
}

/** Empirical formula from the mass (or percent) of each element. */
export function empiricalFromMasses(
  inputs: readonly ElementMass[],
  tolerance = DEFAULT_TOLERANCE
): EmpiricalResult | EmpiricalError {
  if (inputs.length < 2) {
    return { code: "tooFew" };
  }
  const seen = new Set<string>();
  const weights: number[] = [];
  for (const input of inputs) {
    const element = getElement(input.symbol);
    if (!element) {
      return { code: "unknownElement", symbol: input.symbol };
    }
    if (seen.has(input.symbol)) {
      return { code: "duplicateElement", symbol: input.symbol };
    }
    seen.add(input.symbol);
    if (!(input.mass > 0 && Number.isFinite(input.mass))) {
      return { code: "badMass", symbol: input.symbol };
    }
    weights.push(element.weight);
  }
  const ratio = wholeNumberRatio(
    inputs.map((input, i) => ({
      symbol: input.symbol,
      amount: input.mass / weights[i],
    })),
    tolerance
  );
  if (ratio === null) {
    return { code: "noRatio" };
  }
  const steps = ratio.rows.map((row, i) => ({
    ...row,
    mass: inputs[i].mass,
    weight: weights[i],
  }));
  return {
    steps,
    multiplier: ratio.multiplier,
    worstDeviation: ratio.worstDeviation,
    formula: formulaText(steps),
    formulaMass: steps.reduce(
      (sum, step) => sum + step.subscript * step.weight,
      0
    ),
  };
}

export type CombustionError =
  | { code: "badInput" }
  | { code: "exceedsSample" }
  | { code: "noRoomForOxygen" };

export interface CombustionMasses {
  carbon: number;
  hydrogen: number;
  /** Mass left over when the rest is oxygen; null when oxygen is not assumed. */
  oxygen: number | null;
}

/**
 * Masses of C, H (and O by difference) from the CO₂ and H₂O a sample burns to.
 *
 *   m(C) = m(CO₂) × M(C) / M(CO₂)       m(H) = m(H₂O) × 2 M(H) / M(H₂O)
 */
export function combustionMasses(
  sample: number,
  co2: number,
  water: number,
  restIsOxygen: boolean
): CombustionMasses | CombustionError {
  const c = getElement("C")?.weight ?? 0;
  const h = getElement("H")?.weight ?? 0;
  const o = getElement("O")?.weight ?? 0;
  if (
    !(
      [sample, co2, water].every((n) => Number.isFinite(n) && n >= 0) &&
      sample > 0
    )
  ) {
    return { code: "badInput" };
  }
  const carbon = (co2 * c) / (c + 2 * o);
  const hydrogen = (water * 2 * h) / (2 * h + o);
  const rest = sample - carbon - hydrogen;
  if (rest < -1e-9 * sample) {
    return { code: "exceedsSample" };
  }
  if (!restIsOxygen) {
    return { carbon, hydrogen, oxygen: null };
  }
  if (rest <= 1e-6 * sample) {
    return { code: "noRoomForOxygen" };
  }
  return { carbon, hydrogen, oxygen: rest };
}
